import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { IncClickAwayPopper, Placement, useInputState } from "@inception/ui";
import IncDropDownV2Content from "./IncDropDownV2Content";
import { PopperOption } from "./types";
import { useTreeSelection } from "./useTreeSelection";

export interface IncDropDownV2Props<T = string> {
  anchor: React.ReactElement;
  options?: Array<PopperOption<T>>;

  selected?: T[];
  onChange?: (next: T[]) => void;

  multi?: boolean;
  placement?: Placement;
  searchPlaceholder?: string;
  listHeight?: number;

  className?: string;
  popperClassName?: string;

  isOpen?: boolean;
  onClose?: () => void;
  width?: number;
  requireSelection?: boolean;
  actionJSX?: React.ReactNode;
  headerJSX?: React.ReactNode;

  /**
   * When enabled, treats top-level parent options with children as non-selectable group headers.
   *
   * Format:
   * - Top-level options with children become group headers (non-selectable, display-only)
   * - Group headers show the count of children in parentheses, e.g., "Weather (3)"
   * - Children within groups are the selectable items
   * - Selection controls (radio buttons or checkboxes) are determined by multi prop (false = radio buttons, true = checkboxes)
   * - Groups can be expanded/collapsed using the chevron icon
   *
   * Example structure:
   * ```
   * options={[
   *   {
   *     value: "weather-group",
   *     label: "Weather",
   *     children: [
   *       { value: "openweather", label: "OpenWeather" },
   *       { value: "accuweather", label: "AccuWeather" },
   *       { value: "tomorrow", label: "Tomorrow.io" }
   *     ]
   *   },
   *   {
   *     value: "traffic-group",
   *     label: "Traffic",
   *     children: [
   *       { value: "google-maps", label: "Google Maps" },
   *       { value: "waze", label: "Waze" }
   *     ]
   *   }
   * ]}
   * ```
   *
   * This will render as:
   * - "Weather (3)" [collapsible header]
   *   - ○ OpenWeather (radio when multi=false, checkbox when multi=true)
   *   - ○ AccuWeather
   *   - ○ Tomorrow.io
   * - "Traffic (2)" [collapsible header]
   *   - ○ Google Maps
   *   - ○ Waze
   */
  parentAsHeader?: boolean;

  children?: React.ReactNode;
}

const IncDropDownV2 = <T,>({
  anchor,
  options = [],
  selected = [],
  onChange,
  multi = true,
  placement = "bottom-start",
  searchPlaceholder = "Search…",
  listHeight = 260,
  className = "",
  popperClassName = "",
  isOpen,
  onClose,
  width,
  requireSelection = false,
  children,
  actionJSX,
  headerJSX,
  parentAsHeader = false
}: IncDropDownV2Props<T>) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const {
    inputValue: searchInputValue,
    onInputChange: onSearchInputChange,
    setInputValue: setSearch
  } = useInputState();
  const anchorRef = useRef<HTMLDivElement>(null);

  // Use external control if provided, otherwise use internal state
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      if (isOpen !== undefined && onClose) {
        // Controlled mode
        if (typeof value === "function") {
          onClose();
        } else if (!value) {
          onClose();
        }
      } else {
        // Uncontrolled mode
        setInternalOpen(value);
      }
    },
    [isOpen, onClose]
  );

  const [draft, setDraft] = useState<T[]>(selected);

  // Use tree selection hook for parent/child logic
  const { setExpandedParents, getAllEnabledLeafValues, renderRow } = useTreeSelection<T>({
    options,
    multi,
    parentAsHeader
  });

  useEffect(() => {
    if (open) {
      setDraft(selected);
      setSearch("");
      setExpandedParents(new Set()); // Reset expansion state
    }
  }, [open, selected, setSearch, setExpandedParents]);

  // Filter with nested support
  const filtered = useMemo(() => {
    if (!searchInputValue.trim()) {
      return options;
    }

    const lc = searchInputValue.trim().toLowerCase();

    const filterRecursive = (opts: Array<PopperOption<T>>): Array<PopperOption<T>> =>
      opts.reduce(
        (acc, o) => {
          const matchesLabel = o.label.toLowerCase().includes(lc);
          const filteredChildren = o.children ? filterRecursive(o.children) : [];

          // Include if label matches OR any child matches
          if (matchesLabel || filteredChildren.length > 0) {
            acc.push({
              ...o,
              children: filteredChildren.length > 0 ? filteredChildren : o.children
            });
          }

          return acc;
        },
        [] as Array<PopperOption<T>>
      );

    const result = filterRecursive(options);

    // Auto-expand parents when searching and children match
    if (searchInputValue.trim()) {
      const parentsToExpand = new Set<T>();
      result.forEach(opt => {
        if (opt.children && opt.children.length > 0) {
          parentsToExpand.add(opt.value);
        }
      });
      setExpandedParents(parentsToExpand);
    }

    return result;
  }, [options, searchInputValue, setExpandedParents]);

  // Select all with nested support
  const selectAll = useCallback(() => {
    const enabledLeafValues = getAllEnabledLeafValues(filtered);
    const allEnabledSelected = enabledLeafValues.every(value => draft.includes(value));

    if (allEnabledSelected) {
      // Deselect all
      setDraft([]);
    } else {
      // Select all enabled leaf values
      setDraft(enabledLeafValues);
    }
  }, [filtered, draft, getAllEnabledLeafValues]);

  const noSelectionYet = multi && requireSelection && draft.length === 0;

  const handleDone = () => {
    if (noSelectionYet) {
      return;
    }
    onChange?.(draft);
    setOpen(false);
  };

  // Extract the listBody into the new component
  const listBody = (
    <IncDropDownV2Content
      actionJSX={actionJSX}
      draft={draft}
      filtered={filtered}
      getAllEnabledLeafValues={getAllEnabledLeafValues}
      handleDone={handleDone}
      headerJSX={headerJSX}
      listHeight={listHeight}
      multi={multi}
      noSelectionYet={noSelectionYet}
      onChange={onChange}
      onSearchInputChange={onSearchInputChange}
      popperClassName={popperClassName}
      renderRow={(o, level = 0) => renderRow(o, level, draft, setDraft)}
      searchInputValue={searchInputValue}
      searchPlaceholder={searchPlaceholder}
      selectAll={selectAll}
      setOpen={setOpen}
      width={width}
    >
      {children}
    </IncDropDownV2Content>
  );

  if (isOpen !== undefined && onClose) {
    if (open) {
      return (
        <IncClickAwayPopper
          anchorEl={anchor.props.ref?.current || document.body}
          onClickAway={onClose}
          placement={placement}
          show={open}
        >
          {listBody}
        </IncClickAwayPopper>
      );
    }
    return null;
  }

  return (
    <>
      <div
        className={className}
        onClick={() => setOpen(o => !o)}
        ref={anchorRef}
      >
        {anchor}
      </div>

      {open && anchorRef.current && (
        <IncClickAwayPopper
          anchorEl={anchorRef.current}
          onClickAway={() => setOpen(false)}
          placement={placement}
          show={open}
        >
          {listBody}
        </IncClickAwayPopper>
      )}
    </>
  );
};

export default IncDropDownV2;
